import { useEffect, useMemo, useState } from "react";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import { getUsedVourcherHistory } from "api/Vourcher";

interface HistoryItem {
    date: string; // YYYY-MM-DD
    code: string;
    content: string;
    order: string;
}

type HistoryApiItem = Record<string, unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function readByPath(item: HistoryApiItem, path: string): unknown {
    const keys = path.split(".");
    let current: unknown = item;

    for (const key of keys) {
        if (!isRecord(current)) return undefined;
        current = current[key];
    }

    return current;
}

function readStringByPaths(item: HistoryApiItem, paths: string[], fallback = ""): string {
    for (const path of paths) {
        const value = readByPath(item, path);

        if (typeof value === "string" && value.trim()) {
            return value;
        }
        if (typeof value === "number") {
            return String(value);
        }
    }

    return fallback;
}

function normalizeHistoryItem(rawItem: unknown): HistoryItem {
    if (!isRecord(rawItem)) {
        return {
            date: "",
            code: "-",
            content: "-",
            order: "-",
        };
    }

    const item: HistoryApiItem = rawItem;
    const dateRaw = readStringByPaths(item, [
        "used_at",
        "used_date",
        "updated_at",
        "created_at",
        "order.created_at",
        "date",
    ]);
    const parsedDate = dayjs(dateRaw);

    return {
        date: parsedDate.isValid() ? parsedDate.format("YYYY-MM-DD") : "",
        code: readStringByPaths(item, ["template.code", "voucher_code", "code"], "-"),
        content: readStringByPaths(
            item,
            ["template.name", "voucher_name", "voucher_description", "content", "description"],
            "-"
        ),
        order: readStringByPaths(item, ["order.code", "order_code", "order_no", "used_order_id", "order_id"], "-"),
    };
}

export default function VoucherHistory() {
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [toDate, setToDate] = useState<Dayjs | null>(null);
    const [data, setData] = useState<HistoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);

    const startDate = useMemo(() => fromDate?.format("YYYY-MM-DD"), [fromDate]);
    const endDate = useMemo(() => toDate?.format("YYYY-MM-DD"), [toDate]);

    const loadHistory = async (nextPage: number, start?: string, end?: string) => {
        setLoading(true);

        try {
            const res = await getUsedVourcherHistory(nextPage, start, end);

            if (!res?.status) {
                setData([]);
                setPage(1);
                setLastPage(1);

                if (res?.msg) {
                    toast.error(res.msg, { position: "bottom-right" });
                }
                return;
            }

            const items = Array.isArray(res?.data?.items) ? res.data.items : [];
            setData(items.map(normalizeHistoryItem));
            setPage(Number((res as any)?.data?.current_page) || nextPage);
            setLastPage(Number((res as any)?.data?.last_page) || 1);
        } catch (error) {
            setData([]);
            setPage(1);
            setLastPage(1);
            toast.error("Không thể tải lịch sử dùng mã.", { position: "bottom-right" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadHistory(1, startDate, endDate);
    }, []);

    const handleSearch = () => {
        if (fromDate && toDate && fromDate.isAfter(toDate, "day")) {
            toast.warning("Ngày bắt đầu không được lớn hơn ngày kết thúc.", {
                position: "bottom-right",
            });
            return;
        }

        loadHistory(1, startDate, endDate);
    };

    const handlePrevPage = () => {
        if (page <= 1 || loading) return;
        loadHistory(page - 1, startDate, endDate);
    };

    const handleNextPage = () => {
        if (page >= lastPage || loading) return;
        loadHistory(page + 1, startDate, endDate);
    };

    return (
        <div className="MuiGrid-root styles_historyVoucherCtn__g_2M3 MuiGrid-container" style={{ backgroundColor: "rgb(244, 247, 252)" }}>
            {/* TITLE */}
            <div className="MuiGrid-root styles_title___1eh1 MuiGrid-container MuiGrid-item MuiGrid-grid-xs-12">
                <p className="MuiTypography-root MuiTypography-body1">Lịch sử dùng mã giảm giá</p>
            </div>

            <div className="filter-history-voucher">
                {/* FROM DATE */}
                <div className="one-item" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontFamily: "ggsr", width: "26px" }}>Từ</span>
                    <DatePicker
                        format="DD/MM/YYYY"
                        placeholder="ngày/tháng/năm"
                        value={fromDate}
                        onChange={(d) => setFromDate(d)}
                        style={{ width: "100%", height: "38px" }}
                    />
                </div>

                {/* TO DATE */}
                <div className="one-item" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontFamily: "ggsr", width: "26px" }}>Đến</span>
                    <DatePicker
                        format="DD/MM/YYYY"
                        placeholder="ngày/tháng/năm"
                        value={toDate}
                        onChange={(d) => setToDate(d)}
                        style={{ width: "100%", height: "38px" }}
                    />
                </div>

                {/* SEARCH BUTTON */}
                <div className="one-item" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <button
                        className="MuiButtonBase-root MuiButton-root MuiButton-text sc-2362e412-0 iQEreR styles_search__0DsBP"
                        type="button"
                        onClick={handleSearch}
                    >
                        <span className="MuiButton-label">Tìm kiếm</span>
                        <span className="MuiTouchRipple-root" />
                    </button>
                </div>
            </div>

            {/* TABLE */}
            <div className="MuiGrid-root styles_wrapTable__psp3y MuiGrid-container">
                <div className="MuiPaper-root styles_root__6UJMC MuiPaper-elevation1 MuiPaper-rounded">
                    <div className="MuiTableContainer-root styles_headTable__zFdJl">
                        <table className="MuiTable-root MuiTable-stickyHeader" aria-label="sticky table">
                            <thead className="MuiTableHead-root">
                                <tr className="MuiTableRow-root MuiTableRow-head">
                                    <th className="MuiTableCell-root styles_tablle_origin__fWoHy MuiTableCell-head MuiTableCell-alignLeft MuiTableCell-stickyHeader">
                                        Ngày sử dụng
                                    </th>
                                    <th className="MuiTableCell-root styles_tablle_origin__fWoHy MuiTableCell-head MuiTableCell-alignLeft MuiTableCell-stickyHeader">
                                        Mã code giảm giá
                                    </th>
                                    <th className="MuiTableCell-root styles_tablle_origin__fWoHy MuiTableCell-head MuiTableCell-alignLeft MuiTableCell-stickyHeader">
                                        Nội dung
                                    </th>
                                    <th className="MuiTableCell-root styles_tablle_origin__fWoHy MuiTableCell-head MuiTableCell-alignRight MuiTableCell-stickyHeader">
                                        Đơn hàng liên quan
                                    </th>
                                </tr>
                            </thead>

                            <tbody className="MuiTableBody-root">
                                {loading ? (
                                    <tr className="MuiTableRow-root MuiTableRow-hover">
                                        <th className="MuiTableCell-root MuiTableCell-body MuiTableCell-alignCenter" colSpan={4} style={{ display: "table-cell" }}>
                                            <p className="MuiTypography-root MuiTypography-body1" style={{ fontFamily: "ggsm" }}>
                                                Đang tải lịch sử dùng mã...
                                            </p>
                                        </th>
                                    </tr>
                                ) : data.length === 0 ? (
                                    <tr className="MuiTableRow-root MuiTableRow-hover">
                                        <th className="MuiTableCell-root MuiTableCell-body MuiTableCell-alignCenter" colSpan={4} style={{ display: "table-cell" }}>
                                            <p className="MuiTypography-root MuiTypography-body1" style={{ fontFamily: "ggsm" }}>
                                                Bạn chưa dùng mã nào
                                            </p>
                                        </th>
                                    </tr>
                                ) : (
                                    data.map((item, idx) => (
                                        <tr className="MuiTableRow-root MuiTableRow-hover" key={`${item.code}-${item.order}-${idx}`}>
                                            <td className="MuiTableCell-root MuiTableCell-body">{item.date ? dayjs(item.date).format("DD/MM/YYYY") : "-"}</td>
                                            <td className="MuiTableCell-root MuiTableCell-body">{item.code}</td>
                                            <td className="MuiTableCell-root MuiTableCell-body">{item.content}</td>
                                            <td className="MuiTableCell-root MuiTableCell-body MuiTableCell-alignRight">{item.order}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="MuiGrid-root styles_wrapViewMore__eKNS_ MuiGrid-container MuiGrid-align-items-xs-center MuiGrid-justify-content-xs-center" style={{ gap: 10 }}>
                <button
                    className="MuiButtonBase-root MuiButton-root MuiButton-text"
                    type="button"
                    onClick={handlePrevPage}
                    disabled={loading || page <= 1}
                >
                    Trang trước
                </button>
                <p className="MuiTypography-root MuiTypography-body2" style={{ margin: 0 }}>
                    Trang {page}/{lastPage}
                </p>
                <button
                    className="MuiButtonBase-root MuiButton-root MuiButton-text"
                    type="button"
                    onClick={handleNextPage}
                    disabled={loading || page >= lastPage}
                >
                    Trang sau
                </button>
            </div>
        </div>
    );
}